import {
  IUserState,
  Types,
  IUser,
  IUserPreference,
  ECustomerTypeView,
  IUserPermissions,
  IUserRoles
} from './types';
import { Reducer } from 'redux';
import { ICustomer } from '../customers/types';
import { deleteCookie } from '../../../services/MVP/utils';

const INITIAL_STATE: IUserState = {
  id: '',
  name: '',
  email: '',
  facebookId: '',
  linkedinId: '',
  status: '',
  isManager: false,
  phones: [],
  preferences: [],
  roles: {
    adminCustomers: [],
    financialCustomers: [],
    ordersInputCustomers: [],
    ordersViewCustomers: [],
    ordersViewPriceCustomers: [],
    fabricationViewCustomers: []
  },
  sendOrdersToEmail: false,
  sendFinancialToEmail: false,

  userPermissions: {
    hasFinancialAccess: false,
    hasOrderViewAccess: false,
    hasOrderInputAccess: false,
    hasAccessManagementAccess: false,
    hasFabricationAccess: false,
    hasLoyaltyAccess: false
  },

  expecialSteel: false,

  terms: {
    userAcceptNotification: false,
    improvementResearch: false,
    privacyTerms: false,
    gerdauProModal: false
  },

  searchCustomerType: ECustomerTypeView.EMITTER,
  loading: false,
  error: false,
  savingUser: false,
  errorSavingUser: false,

  coreTeam: false
};

type TCustomAction = {
  type: Types;
  payload: {
    userId: string;
    expecialSteel: boolean;

    user: IUser;
    userPreference?: IUserPreference;
    sendOrdersToEmail?: boolean;
    sendFinancialToEmail?: boolean;
    userAcceptNotification?: boolean;

    telephone: string;
    jobTitle: string;
    birthdate: string;

    customers: ICustomer[];
  };
};

const deleteAuthCookies = () => {
  const cookieNames: string[] = [
    '@access_token',
    '@authorization-token',
    '@authorization-token-expiration',
    '@sso-access'
  ];

  cookieNames.forEach((cookieName: string) => {
    const cookies = document.cookie.split(';');

    cookies.forEach((cookie: string) => {
      const cookiePair = cookie.split('=');
      const cookieNameTrimmed = cookiePair[0].trim();

      if (cookieNameTrimmed === cookieName) {
        document.cookie = `${cookieNameTrimmed}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

        const paths = ['/', '/purchase/long-steel/commerce'];
        paths.forEach((path: string) => {
          document.cookie = `${cookieNameTrimmed}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
        });
      }
    });
    deleteCookie(cookieName);
  });
};

const reducer: Reducer<IUserState, TCustomAction> = (
  state = INITIAL_STATE,
  action
) => {
  switch (action.type) {
    case Types.TOKEN_VALIDATE:
    case Types.LOAD_USER:
      return { ...state, loading: true, error: false };

    case Types.UPDATE_USER_PREFERENCES:
    case Types.SAVE_USER:
      return {
        ...state,
        preferences: action.payload.userPreference
          ? updateUserPreferences(
              state.preferences,
              action.payload.userPreference
            )
          : state.preferences,
        sendOrdersToEmail:
          action.payload.sendOrdersToEmail !== undefined
            ? action.payload.sendOrdersToEmail
            : state.sendOrdersToEmail,
        sendFinancialToEmail:
          action.payload.sendFinancialToEmail !== undefined
            ? action.payload.sendFinancialToEmail
            : state.sendFinancialToEmail,
        terms: {
          ...state.terms,
          userAcceptNotification:
            action.payload.userAcceptNotification !== undefined
              ? action.payload.userAcceptNotification
              : state.terms.userAcceptNotification
        },
        savingUser: true,
        errorSavingUser: false
      };

    case Types.TOKEN_VALIDATE_SUCCESS:
      return {
        ...state,
        loading: false,
        error: false,
        id: action.payload.userId,
        expecialSteel: action.payload.expecialSteel
      };

    case Types.LOAD_USER_SUCCESS:
    case Types.SAVE_USER_SUCCESS:
      return {
        ...state,
        ...action.payload.user,
        userPermissions: {
          ...setUserPermissions(action.payload.user.roles)
        },
        loading: false,
        error: false,
        errorSavingUser: false,
        savingUser: false
      };

    case Types.TOKEN_VALIDATE_FAILURE:
    case Types.LOAD_USER_FAILURE:
      return { ...state, loading: false, error: true };

    case Types.SAVE_USER_FAILURE:
      return { ...state, savingUser: false, errorSavingUser: true };

    case Types.SWAP_USER_VIEW_TYPE:
      return { ...state, loading: true };

    case Types.USER_LOGOUT:
      deleteAuthCookies();
      return { ...INITIAL_STATE };

    default:
      return state;
  }
};

const updateUserPreferences = (
  preferences: IUserPreference[],
  newUserPreference: IUserPreference
): IUserPreference[] => {
  const idx = preferences.findIndex(
    (pref) => pref.name === newUserPreference.name
  );
  preferences[idx] = newUserPreference;
  return preferences;
};

const setUserPermissions = (roles: IUserRoles): IUserPermissions => {
  return {
    hasLoyaltyAccess:
      !!roles.loyaltyCustomers && roles.loyaltyCustomers.length > 0,
    hasFinancialAccess: roles.financialCustomers.length > 0,
    hasOrderViewAccess: roles.ordersViewCustomers.length > 0,
    hasOrderInputAccess: roles.ordersInputCustomers.length > 0,
    hasAccessManagementAccess: roles.adminCustomers.length > 0,
    hasFabricationAccess:
      roles.fabricationViewCustomers !== null &&
      roles.fabricationViewCustomers.length > 0
  };
};

export default reducer;
